import { useEffect } from "react";

// Smaller moves come from inertia or a bounce at the page edge
const SCROLL_DIRECTION_THRESHOLD = 6;
const WHEEL_GESTURE_DELAY = 150;

const PASSIVE_CAPTURE = { capture: true, passive: true };

/**
 * Closes an open dropdown on a click outside it and when the page outside
 * starts scrolling up (wheel or swipe). The direction is taken at the start
 * of a gesture, so an inertial bounce does not change it.
 *
 * @param {object} params
 * @param {boolean} params.isOpen
 * @param {() => void} params.onClose
 * @param {{ current: Element|null }[]} params.insideRefs - control and dropdown
 * @param {string} [params.triggerSelector] - outside button that toggles the dropdown
 */
const useCloseOnOutsideScroll = ({ isOpen, onClose, insideRefs, triggerSelector }) => {
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let touchStartY = null;
    let touchGestureDirection = null;
    let wheelGestureDirection = null;
    let wheelGestureTimer = null;

    const isInside = (target) => {
      if (!(target instanceof Element)) {
        return false;
      }

      const insideElement = insideRefs.some((ref) => ref.current?.contains(target));
      const insideTrigger = Boolean(triggerSelector && target.closest(triggerSelector));

      return insideElement || insideTrigger;
    };

    const handleOutsideClick = (event) => {
      if (!isInside(event.target)) {
        onClose();
      }
    };

    const resetWheelGesture = () => {
      wheelGestureDirection = null;
      wheelGestureTimer = null;
    };

    const handleWheelOutside = (event) => {
      if (isInside(event.target)) {
        return;
      }

      clearTimeout(wheelGestureTimer);
      wheelGestureTimer = setTimeout(resetWheelGesture, WHEEL_GESTURE_DELAY);

      if (Math.abs(event.deltaY) < SCROLL_DIRECTION_THRESHOLD) {
        return;
      }

      if (!wheelGestureDirection) {
        wheelGestureDirection = event.deltaY < 0 ? "up" : "down";
      }

      if (wheelGestureDirection === "up") {
        onClose();
      }
    };

    const resetTouchGesture = () => {
      touchStartY = null;
      touchGestureDirection = null;
    };

    const handleTouchStartOutside = (event) => {
      resetTouchGesture();

      if (!isInside(event.target)) {
        touchStartY = event.touches[0]?.clientY ?? null;
      }
    };

    const handleTouchMoveOutside = (event) => {
      const currentTouchY = event.touches[0]?.clientY;

      if (
        isInside(event.target) ||
        typeof currentTouchY !== "number" ||
        typeof touchStartY !== "number"
      ) {
        return;
      }

      const touchDelta = currentTouchY - touchStartY;

      if (Math.abs(touchDelta) < SCROLL_DIRECTION_THRESHOLD) {
        return;
      }

      // Finger moves down — the page scrolls up
      if (!touchGestureDirection) {
        touchGestureDirection = touchDelta > 0 ? "up" : "down";
      }

      if (touchGestureDirection === "up") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("wheel", handleWheelOutside, PASSIVE_CAPTURE);
    document.addEventListener("touchstart", handleTouchStartOutside, PASSIVE_CAPTURE);
    document.addEventListener("touchmove", handleTouchMoveOutside, PASSIVE_CAPTURE);
    document.addEventListener("touchend", resetTouchGesture, true);
    document.addEventListener("touchcancel", resetTouchGesture, true);

    return () => {
      clearTimeout(wheelGestureTimer);
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("wheel", handleWheelOutside, true);
      document.removeEventListener("touchstart", handleTouchStartOutside, true);
      document.removeEventListener("touchmove", handleTouchMoveOutside, true);
      document.removeEventListener("touchend", resetTouchGesture, true);
      document.removeEventListener("touchcancel", resetTouchGesture, true);
    };
  }, [insideRefs, isOpen, onClose, triggerSelector]);
};

export default useCloseOnOutsideScroll;
